import { Check, ShoppingCart } from 'lucide-react'

import { formatCurrency, formatCurrencyRange } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { getTotalPremium, type PricedQuoteCard, PricingKind } from './quoteCardTypes'

interface QuoteCardMiniProps {
  quote: PricedQuoteCard
  /** Currently shown in the focused viewer (one of the up-to-two compared panels). */
  active: boolean
  /** Every coverage on this quote is already in the cart. */
  inCart?: boolean
  onClick: () => void
}

/**
 * Super-compact quote row — carrier, price, and status flags only. Used as the
 * comparison-rail chip in the focused viewer (and reusable as a checkout cart row).
 */
export function QuoteCardMini({ quote, active, inCart, onClick }: QuoteCardMiniProps) {
  const total = getTotalPremium(quote)
  const isIndication = quote.pricingKind === PricingKind.INDICATION

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition-colors',
        active
          ? 'border-brand-700 bg-brand-50'
          : 'border-ink-100 bg-card hover:border-ink-200 hover:bg-ink-50/60'
      )}
    >
      <img
        src={quote.iconUrl}
        alt={quote.iconAlt}
        className="border-ink-50 size-8 shrink-0 rounded-full border object-contain"
      />
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-foreground truncate text-sm font-bold">
          {quote.carrierName}
          {/* Same brand, different paper — without it two minis read as one repeated quote. */}
          {quote.carrierProgram && <span className="text-ink-400 font-normal"> {quote.carrierProgram}</span>}
        </span>
        <span className={cn('text-xs', isIndication ? 'text-ink-400' : 'text-ink-500')}>
          {quote.totalPremiumRange ? formatCurrencyRange(quote.totalPremiumRange) : formatCurrency(total)}
          <span className="text-ink-300"> /yr</span>
        </span>
      </div>
      {inCart && <ShoppingCart className="text-brand-700 size-4 shrink-0" aria-label="In cart" />}
      {active && <Check className="text-brand-700 size-4 shrink-0" aria-hidden="true" />}
    </button>
  )
}
